import { Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Dash } from '@/components/common/HtmlEntities';
import { RequestSummary } from '@/domain/newRequest';
import { COLOR, FONT_SIZE, FONT_WEIGHT, PX_TO_REM } from '@/globalStyles';
import { DEFAULT_NUMBER_OF_DAYS } from '@/utils/constants/common';

import { displayNumberOfDays, periodText } from '../helpers';

type Props = {
    summary?: RequestSummary;
};

const NoRestriction = ({ summary }: Props) => {
    const { t } = useTranslation();

    const { noLimits, periodType, daysUsed, daysUsedAdditional, isOverLimit, leftDays } = summary || {};
    const color = isOverLimit ? COLOR.redWildWatermelon : '';

    const daysLeftText = useCallback(() => {
        if (!summary) return <Dash />;
        if (noLimits) return t('requestDetails_no_limit');

        const daysToDisplay = isOverLimit ? DEFAULT_NUMBER_OF_DAYS : leftDays;
        const formattedDays = displayNumberOfDays(daysToDisplay, t);
        const periodInfo = !daysUsed ? periodText(periodType, t) : '';

        return `${formattedDays} ${periodInfo}`;
    }, [isOverLimit, leftDays, daysUsed, periodType, t]);

    return (
        <div>
            <SummaryItem>
                <Typography variant="table_text">{t('request_for_title')}:</Typography>
                <TypographyBold color={color}>
                    {daysUsedAdditional ? displayNumberOfDays(daysUsedAdditional, t) : <Dash />}
                </TypographyBold>
            </SummaryItem>

            <SummaryItem>
                <Typography variant="table_text">{t('requestDetails_usedLimit_title')}:</Typography>
                <TypographyBold>
                    {daysUsed ? `${displayNumberOfDays(daysUsed, t)} (${periodText(periodType, t)})` : <Dash />}
                </TypographyBold>
            </SummaryItem>

            <SummaryItem>
                <Typography variant="table_text">{t('left')}:</Typography>
                <TypographyBold>{daysLeftText()}</TypographyBold>
            </SummaryItem>
        </div>
    );
};

const SummaryItem = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    &:not(:last-of-type) {
        margin-bottom: 1rem;
    }
`;

const TypographyBold = styled(Typography)`
    font-weight: ${FONT_WEIGHT.medium} !important;
    font-size: ${FONT_SIZE.smedium};
    padding-left: ${PX_TO_REM[30]};
`;

const Memoized = memo(NoRestriction);
const Styled = styled(Memoized)``;

export default Styled;
